import EVENT_KEY_CODES from '@/utils/KeyCodes';

const NUMBER_KEY_CODES = {
  48: 0,
  49: 1,
  50: 2,
  51: 3,
  52: 4,
  53: 5,
  54: 6,
  55: 7,
  56: 8,
  57: 9,
  96: 0,
  97: 1,
  98: 2,
  99: 3,
  100: 4,
  101: 5,
  102: 6,
  103: 7,
  104: 8,
  105: 9,
};

const ADDITIONAL_ALLOWED_NUMBER_KEYCODES = [8, 13, 16, 17, 18, 37, 38, 39, 40, 45, 46, 110, 188, 190];

const validateKeyEvent = (keyboardEvent, caseSensitive = false, keyCodes = null) => {
  const keyCode = getKeyCode(keyboardEvent.keyCode || keyboardEvent.key || keyboardEvent.code);

  return keyCode ? keyCodes.includes(keyCode) : false;
};

const isNumeric = (keyboardEvent) => (validateKeyEvent(keyboardEvent, false, Object.keys(NUMBER_KEY_CODES)
  .map((k) => parseInt(k))) && !keyboardEvent.shiftKey) || keyboardEvent.ctrlKey;

const isNumberKey = (keyboardEvent) => validateKeyEvent(keyboardEvent, false, Object.keys(NUMBER_KEY_CODES)
  .map((k) => parseInt(k))
  .concat(ADDITIONAL_ALLOWED_NUMBER_KEYCODES)) || keyboardEvent.ctrlKey;

const getKeyNumber = (keyboardEvent) => {
  const keyNumber = NUMBER_KEY_CODES[getKeyCode(keyboardEvent.keyCode || keyboardEvent.key || keyboardEvent.code)];

  return typeof keyNumber === 'number' ? keyNumber : null;
};

const isBackspace = (keyboardEvent) => isKey(keyboardEvent, 'Backspace');

const isEnter = (keyboardEvent) => isKey(keyboardEvent, 'Enter');

const isTab = (keyboardEvent) => isKey(keyboardEvent, 'Tab');

const isArrowUp = (keyboardEvent) => isKey(keyboardEvent, 'ArrowUp');

const isArrowDown = (keyboardEvent) => isKey(keyboardEvent, 'ArrowDown');

const isArrowLeft = (keyboardEvent) => isKey(keyboardEvent, 'ArrowLeft');

const isArrowRight = (keyboardEvent) => isKey(keyboardEvent, 'ArrowRight');

const isPeriod = (keyboardEvent) => isKey(keyboardEvent, 'Period');

const isComma = (keyboardEvent) => isKey(keyboardEvent, 'Comma');

const keyCodeFilter = (EVENT_KEY_CODE, key, caseSensitive) => {
  const hasKey = EVENT_KEY_CODES[EVENT_KEY_CODE].keys
    .map((k) => (caseSensitive ? k : `${k}`.trim().toLowerCase()))
    .includes(caseSensitive ? key : `${key}`.trim().toLowerCase());
  const hasCode = EVENT_KEY_CODES[EVENT_KEY_CODE].codes
    .map((c) => (caseSensitive ? c : `${c}`.trim().toLowerCase()))
    .includes(caseSensitive ? key : `${key}`.trim().toLowerCase());

  return hasKey || hasCode;
};

const getKeyCodes = (key, options = {
  caseSensitive: false,
}) => {
  const {
    caseSensitive,
  } = options;

  let keyCodes = [];

  if (typeof key === 'number') {
    keyCodes = [key];
  } else if (typeof key === 'string') {
    keyCodes = Object.keys(EVENT_KEY_CODES).filter((EVENT_KEY_CODE) => keyCodeFilter(EVENT_KEY_CODE, key, caseSensitive)).map((kc) => parseInt(kc));
  }

  return keyCodes;
};

const getKeyCode = (key, options = {
  caseSensitive: false,
}) => {
  const {
    caseSensitive,
  } = options;

  let keyCode;

  if (typeof key === 'number') {
    keyCode = key;
  } else if (typeof key === 'string') {
    keyCode = Object.keys(EVENT_KEY_CODES).find((EVENT_KEY_CODE) => keyCodeFilter(EVENT_KEY_CODE, key, caseSensitive));
  }

  return keyCode ? parseInt(keyCode) : null;
};

const isKey = (keyboardEvent, key, options = {
  caseSensitive: false,
  isCtrl: null,
  isShift: null,
  isAlt: null,
  isMeta: null,
}) => {
  let matches = false;
  const keysToCheck = Array.isArray(key) ? key : [key];

  for (let i = 0; i < keysToCheck.length; i++) {
    const eventKeyCode = getKeyCode(keyboardEvent.keyCode || keyboardEvent.key || keyboardEvent.code, options);
    const keyCodes = getKeyCodes(key, options);
    matches = keyCodes.includes(eventKeyCode);

    if (matches) {
      break;
    }
  }

  const {
    isCtrl,
    isShift,
    isAlt,
    isMeta,
  } = options;

  let optionMatches = true;

  if (isCtrl) {
    optionMatches = optionMatches && keyboardEvent.ctrlKey;
  }

  if (isShift) {
    optionMatches = optionMatches && keyboardEvent.shiftKey;
  }

  if (isAlt) {
    optionMatches = optionMatches && keyboardEvent.altKey;
  }

  if (isMeta) {
    optionMatches = optionMatches && keyboardEvent.metaKey;
  }

  return matches && optionMatches;
};

export default {
  isKey,
  isNumeric,
  isNumberKey,
  getKeyNumber,
  isBackspace,
  isEnter,
  isTab,
  isArrowUp,
  isArrowRight,
  isArrowDown,
  isArrowLeft,
  isPeriod,
  isComma,
};
